import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import { getLogislyLocationsApi } from "../api/logislyOrderTakerApi";
import {
  createLogislyLocationGroupApi,
  deleteLogislyLocationGroupApi,
  getLogislyLocationGroupsApi,
  updateLogislyLocationGroupApi,
  type LogislyLocationGroupPayload,
} from "../api/logislyLocationGroupApi";
import type { RootState } from "../store";
import type { LogislyLocation } from "../types/logislyOrderTaker";
import type { LogislyLocationGroup } from "../types/logislyLocationGroup";
import { isUserAdmin, UserRole } from "../types/user";

const isLinked = (location: LogislyLocation) => location.linkStatus === "LINKED";
const sortLocations = (a: LogislyLocation, b: LogislyLocation) => a.name.localeCompare(b.name);
type DropPosition = "before" | "after";
const getErrorMessage = (error: unknown) => {
  if (typeof error === "object" && error && "response" in error) {
    const response = error.response as { data?: { error?: { message?: string } } };
    return response.data?.error?.message ?? "Unable to save location group.";
  }
  return "Unable to save location group.";
};

interface LocationGroupDialogProps {
  group: LogislyLocationGroup | null;
  locations: LogislyLocation[];
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: LogislyLocationGroupPayload) => Promise<void>;
}

const LocationGroupDialog = ({
  group,
  locations,
  open,
  onClose,
  onSubmit,
}: LocationGroupDialogProps) => {
  const [name, setName] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<LogislyLocation[]>([]);
  const [draggedLocationId, setDraggedLocationId] = useState<string>();
  const [locationDropTarget, setLocationDropTarget] = useState<{
    id: string;
    position: DropPosition;
  }>();
  const locationPointerDragRef = useRef<
    { id: string; startX: number; startY: number; moved: boolean } | undefined
  >(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? "");
    setSelectedLocations(
      group
        ? group.logislyLocationIds.flatMap((id) => {
            const location = locations.find((item) => item.logislyLocationId === id);
            return location ? [location] : [];
          })
        : [],
    );
    setDraggedLocationId(undefined);
    setLocationDropTarget(undefined);
    setError(undefined);
  }, [group, locations, open]);

  const getLocationDropTarget = (sourceId: string, clientX: number, clientY: number) => {
    const target = document
      .elementFromPoint(clientX, clientY)
      ?.closest<HTMLElement>("[data-location-id]");
    const targetId = target?.dataset.locationId;
    if (!target || !targetId || targetId === sourceId) return undefined;
    const bounds = target.getBoundingClientRect();
    const position: DropPosition = clientX < bounds.left + bounds.width / 2 ? "before" : "after";
    return { id: targetId, position };
  };

  const reorderLocations = (sourceId: string, targetId: string, position: DropPosition) => {
    if (sourceId === targetId) return;
    setSelectedLocations((current) => {
      const sourceIndex = current.findIndex((location) => location.logislyLocationId === sourceId);
      const targetIndex = current.findIndex((location) => location.logislyLocationId === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const reordered = [...current];
      const [moved] = reordered.splice(sourceIndex, 1);
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      reordered.splice(adjustedTargetIndex + (position === "after" ? 1 : 0), 0, moved);
      return reordered;
    });
  };

  const submit = async () => {
    if (!name.trim() || selectedLocations.length === 0) return;
    setSubmitting(true);
    setError(undefined);
    try {
      await onSubmit({
        name: name.trim(),
        logislyLocationIds: selectedLocations.map((location) => location.logislyLocationId),
      });
      onClose();
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{group ? "Edit Location Group" : "Add Location Group"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            autoFocus
            label="Group name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 120 }}
            required
          />
          <Autocomplete
            multiple
            options={locations}
            value={selectedLocations}
            onChange={(_, value) => setSelectedLocations(value)}
            getOptionLabel={(location) => location.name}
            isOptionEqualToValue={(a, b) => a.logislyLocationId === b.logislyLocationId}
            renderTags={(value, getTagProps) =>
              value.map((location, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                const isDropTarget = locationDropTarget?.id === location.logislyLocationId;
                return (
                  <Box
                    key={key}
                    component="span"
                    data-location-id={location.logislyLocationId}
                    onPointerDown={(event) => {
                      if (
                        event.button !== 0 ||
                        (event.target as HTMLElement).closest(".MuiChip-deleteIcon")
                      ) {
                        return;
                      }
                      event.currentTarget.setPointerCapture(event.pointerId);
                      locationPointerDragRef.current = {
                        id: location.logislyLocationId,
                        startX: event.clientX,
                        startY: event.clientY,
                        moved: false,
                      };
                    }}
                    onPointerMove={(event) => {
                      const drag = locationPointerDragRef.current;
                      if (!drag) return;
                      if (
                        !drag.moved &&
                        Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 6
                      ) {
                        return;
                      }
                      drag.moved = true;
                      event.preventDefault();
                      setDraggedLocationId(drag.id);
                      setLocationDropTarget(
                        getLocationDropTarget(drag.id, event.clientX, event.clientY),
                      );
                    }}
                    onPointerUp={(event) => {
                      const drag = locationPointerDragRef.current;
                      locationPointerDragRef.current = undefined;
                      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                      }
                      if (drag?.moved) {
                        const target = getLocationDropTarget(drag.id, event.clientX, event.clientY);
                        if (target) reorderLocations(drag.id, target.id, target.position);
                      }
                      setDraggedLocationId(undefined);
                      setLocationDropTarget(undefined);
                    }}
                    onPointerCancel={() => {
                      locationPointerDragRef.current = undefined;
                      setDraggedLocationId(undefined);
                      setLocationDropTarget(undefined);
                    }}
                    sx={{
                      display: "inline-flex",
                      cursor:
                        draggedLocationId === location.logislyLocationId ? "grabbing" : "grab",
                      touchAction: "none",
                    }}
                  >
                    <Chip
                      {...tagProps}
                      icon={<DragIcon fontSize="small" />}
                      label={location.name}
                      sx={
                        isDropTarget
                          ? locationDropTarget.position === "before"
                            ? { borderLeft: 3, borderColor: "primary.main" }
                            : { borderRight: 3, borderColor: "primary.main" }
                          : undefined
                      }
                    />
                  </Box>
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Locations"
                helperText="Only linked Logisly locations can be included. Drag chips to set their order."
                required
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || !name.trim() || selectedLocations.length === 0}
        >
          {group ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LogislyLocationGroupsPage = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const canManage = isUserAdmin(user) || user?.role === UserRole.STAFF;
  const [groups, setGroups] = useState<LogislyLocationGroup[]>([]);
  const [locations, setLocations] = useState<LogislyLocation[]>([]);
  const [dialogGroup, setDialogGroup] = useState<LogislyLocationGroup | null | undefined>();
  const [groupToDelete, setGroupToDelete] = useState<LogislyLocationGroup | null>(null);
  const [loadError, setLoadError] = useState<string>();
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoadError(undefined);
    try {
      const [groupData, locationData] = await Promise.all([
        getLogislyLocationGroupsApi(),
        getLogislyLocationsApi(true),
      ]);
      setGroups(groupData);
      setLocations(locationData);
    } catch (error) {
      setLoadError(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const linkedLocations = useMemo(
    () => [...locations.filter(isLinked)].sort(sortLocations),
    [locations],
  );
  const locationNameById = useMemo(
    () => new Map(locations.map((location) => [location.logislyLocationId, location.name])),
    [locations],
  );
  const saveGroup = async (payload: LogislyLocationGroupPayload) => {
    if (dialogGroup) await updateLogislyLocationGroupApi(dialogGroup._id, payload);
    else await createLogislyLocationGroupApi(payload);
    await load();
  };
  const deleteGroup = async () => {
    if (!groupToDelete) return;
    setDeleting(true);
    try {
      await deleteLogislyLocationGroupApi(groupToDelete._id);
      setGroupToDelete(null);
      await load();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Location Groups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reuse saved destination sets when creating Order Taker Configs V2.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canManage}
          onClick={() => setDialogGroup(null)}
        >
          Add group
        </Button>
      </Box>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {groups.length === 0 ? (
        <Typography color="text.secondary">No location groups yet.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Group</TableCell>
              <TableCell>Locations</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group._id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{group.name}</TableCell>
                <TableCell>
                  {group.logislyLocationIds
                    .map((id) => locationNameById.get(id) ?? `Missing location (${id})`)
                    .join(", ")}
                </TableCell>
                {canManage && (
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setDialogGroup(group)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setGroupToDelete(group)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <LocationGroupDialog
        open={dialogGroup !== undefined}
        group={dialogGroup ?? null}
        locations={linkedLocations}
        onClose={() => setDialogGroup(undefined)}
        onSubmit={saveGroup}
      />
      <ConfirmationDialog
        open={Boolean(groupToDelete)}
        onClose={() => setGroupToDelete(null)}
        onConfirm={deleteGroup}
        title="Delete Location Group"
        message={`Delete ${groupToDelete?.name ?? "this location group"}? Existing configs will not be changed.`}
        confirmButtonText="Delete"
        loading={deleting}
      />
    </Box>
  );
};

export default LogislyLocationGroupsPage;
